import { DropboxFileItem, DropboxFileListLoadedPathsParams } from '@common/types/dropbox';
import { api } from '@services/api';

type ToggleRepairdocFavoriteArgs = {
    path: string;
    folderPath: string;
};

const repairdocsDropboxApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getRepairdocDropboxFilesLoadedPaths: builder.query<
            {
                path: string;
                type: 'repair_documents';
                results: DropboxFileItem[];
                /**
                 * Remaining count of items that can be loaded for the given path.
                 * This is useful for determining if there are more items to load, in a infinite scroll scenario.
                 * If the count is 0, it means there are no more items to load for the given path.
                 */
                count: number;
            },
            DropboxFileListLoadedPathsParams<'repair_documents'>
        >({
            query: (params) => ({
                url: `dropbox/files/`,
                method: 'POST',
                body: {
                    ...params,
                    type: 'repair_documents',
                },
            }),
            providesTags: (_res, _err, arg) => [
                {
                    type: 'RepairdocsDropboxFiles',
                    id: `PATH:${arg.path ?? ''}-LOADED_PATHS:${arg.loaded_paths?.join(',') ?? ''}-${
                        arg.page_size ?? ''
                    }-${arg.ordering ?? ''}`,
                },
                {
                    type: 'RepairdocsDropboxFiles',
                    id: `PATH:${arg.path ?? ''}`,
                },
            ],
        }),

        getRepairdocDropboxDownloadJson: builder.query<
            { type: 'repair_documents' } & Pick<
                DropboxFileItem,
                'path' | 'download_url' | 'stream_url' | 'url_expires_at'
            >,
            string
        >({
            query: (path) => ({
                url: `dropbox/download/`,
                params: {
                    path,
                    type: 'repair_documents',
                    response_type: 'json',
                },
            }),
        }),
        repairdocFavorite: builder.mutation<void, ToggleRepairdocFavoriteArgs>({
            query: ({ path }) => ({
                url: `dropbox/favorites/`,
                method: 'POST',
                body: { path, type: 'repair_documents' },
            }),
            invalidatesTags: (_res, _err, { path, folderPath }) => [
                {
                    type: 'RepairdocsDropboxFiles',
                    id: `PATH:${folderPath ?? ''}`,
                },
            ],
        }),
        repairdocUnfavorite: builder.mutation<void, ToggleRepairdocFavoriteArgs>({
            query: ({ path }) => ({
                url: `dropbox/favorites/`,
                params: { path, type: 'repair_documents' },
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { path, folderPath }) => [
                {
                    type: 'RepairdocsDropboxFiles',
                    id: `PATH:${folderPath ?? ''}`,
                },
            ],
        }),
    }),
});

export const {
    useRepairdocFavoriteMutation,
    useRepairdocUnfavoriteMutation,
    useLazyGetRepairdocDropboxFilesLoadedPathsQuery,
    useLazyGetRepairdocDropboxDownloadJsonQuery,
} = repairdocsDropboxApi;
export default repairdocsDropboxApi;
